import { playVideo, setConstraints, nextCameraConstraints, showAudioLevels, showConnectError, clearConnectError, cameraErrorKey, hostLink, renderQR, copyWithFeedback, setPressed, getTrustedParents, isTrustedParent, addTrustedParent, removeTrustedParent, renameTrustedParent, openToast, closeToast, withViewTransition } from './utils.js';
import { track, notePeerRecovery, EVENTS } from './analytics.js';
import { createCryDetector } from './cry-analyser.js';
import { applyVideoSendCaps } from './encoding.js';
import { attachIceRecovery } from './ice-restart.js';
import { notifyCry, sendGoodbye } from './push.js';
import { t, tCount } from './i18n.js';

// Parcel resolves this to the hashed, bundled asset URL at build time.
const MELODY_URL = new URL('sleep-melody.mp3', import.meta.url);

// Auto-stop the lullaby after this long. It loops, and cry detection is muted
// while it plays (the melody would trip it), so an unbounded lullaby could
// silence cry alerts indefinitely. Capping it keeps that blind spot bounded.
const MELODY_MAX_MS = 10 * 60 * 1000;

// Auto-dismiss an unanswered "new parent" heads-up (as if Ignored) so it can't
// linger on screen or stall the queue behind it if the host never taps. The
// parent keeps watching either way; they'll simply prompt again next session.
const TRUST_PROMPT_MS = 15 * 1000;

// Warn parents once the monitor's battery drops below this (and isn't charging).
// A dying monitor silently kills the whole feed, so parents — who are away from
// the room — get a heads-up while there's still time to plug it in.
const BATTERY_LOW = 0.20;

// How often the monitor broadcasts a live-video heartbeat to parents. It carries
// a counter of genuine camera frames (see startLiveHeartbeat), so a parent can
// confirm the *source* is actually producing new pictures — not just that bytes
// or decoded frames are arriving (a re-encoded frozen frame would fake those).
const LIVE_HEARTBEAT_MS = 2 * 1000;

// The lullaby plays out of the baby monitor's speaker, but a parent can start
// it remotely. A remote command carries no user gesture on the monitor, so the
// browser's autoplay policy would reject the very first Audio.play() unless the
// element was already "unblocked" by an earlier gesture on this device. That's
// why, before this priming, a parent could only start the melody *after* the
// monitor had played it once locally. We keep the element as a module-level
// singleton so it can be unlocked at role-pick time (a real tap) and reused by
// the Host instance created moments later.
let melodyAudio = null;

// A camera/mic stream acquired during the "baby monitor" tap. iOS Safari only
// shows the getUserMedia permission prompt under transient user activation, and
// that activation is gone by the time the peer has opened and Host is built (it
// sits behind an async ICE fetch + a signalling round-trip). Requesting the
// camera there — as we used to — makes iOS reject with NotAllowedError on the
// first run. Priming it inside the real tap keeps the prompt within activation;
// Host.startCamera() then adopts this stream instead of re-requesting.
let cameraPrime = null;

// Call from a genuine user gesture (choosing the "baby monitor" role). Kicks off
// getUserMedia immediately and caches the promise. Best-effort: if it can't even
// start, startCamera() falls back to a fresh request.
export function primeCamera() {
  if (cameraPrime) return cameraPrime;
  try {
    cameraPrime = navigator.mediaDevices.getUserMedia({
      video: { frameRate: { ideal: 15, max: 20 } },
      audio: { echoCancellation: true },
    });
    // startCamera() awaits this same promise and surfaces any error properly;
    // this no-op handler just prevents an "unhandled rejection" in the window
    // between priming and Host being constructed.
    cameraPrime.catch(() => {});
  } catch (e) {
    cameraPrime = null;
  }
  return cameraPrime;
}

// Hand the primed stream promise (if any) to the Host, clearing it so the
// stream is owned in exactly one place.
function takePrimedCamera() {
  const primed = cameraPrime;
  cameraPrime = null;
  return primed;
}

// Stop and drop a primed stream that was never adopted by a Host — e.g. the
// connection failed fatally before the peer opened, which would otherwise leave
// the camera live (indicator on) behind the error screen. No-op once the Host
// has taken the stream (cameraPrime is null by then), so it never kills a live,
// in-use feed.
export function discardPrimedCamera() {
  const primed = cameraPrime;
  cameraPrime = null;
  if (!primed) return;
  primed
    .then((stream) => stream.getTracks().forEach((tr) => tr.stop()))
    .catch(() => {});
}

function getMelodyAudio() {
  if (!melodyAudio) {
    melodyAudio = new Audio(MELODY_URL);
    melodyAudio.loop = true;
    melodyAudio.preload = 'auto';
  }
  return melodyAudio;
}

// A ~0-length silent WAV. We play THIS on the melody element (not the lullaby)
// to satisfy the autoplay gate, so priming is guaranteed silent and can't
// buffer/loop the real track.
const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

// Call this from a genuine user gesture on the baby monitor (choosing the
// "baby monitor" role). Playing a short silent clip on the element inside the
// gesture "blesses" it, so later programmatic playback — including a parent's
// remote start — is allowed by the autoplay policy. iOS keeps that activation
// across a src change, so we then restore the real lullaby source.
//
// Crucially we do NOT play the lullaby itself here: it loops and is long, so a
// delayed pause could leave it audibly playing and, on iOS, hold the audio
// session long enough to block the monitor's camera/mic getUserMedia — which
// looked like being stuck on the connect screen.
export function primeMelody() {
  const audio = getMelodyAudio();
  try {
    const restore = () => {
      try {
        audio.pause();
        audio.loop = true;
        audio.muted = false; // setMelody controls audibility from here on
        audio.src = MELODY_URL.toString();
        audio.load();
      } catch (e) { /* best-effort */ }
    };
    audio.loop = false;
    audio.muted = true;
    audio.src = SILENT_WAV;
    const p = audio.play();
    if (p && p.then) p.then(restore).catch(restore);
    else restore();
  } catch (e) {
    /* If play() throws we simply skip priming; local playback still works. */
  }
}

const $connect = document.getElementById('connect');
const $switchCamera = document.getElementById('switch-camera');
const $toggleVideo = document.getElementById('toggle-video');
const $videoOff = document.getElementById('video-off');
const $addParent = document.getElementById('add-parent');
const $melodyToggle = document.getElementById('melody-toggle');
const $copyLink = document.getElementById('copy-link');
const $hostStatus = document.getElementById('host-status');
const $trustPrompt = document.getElementById('trust-prompt');
const $manageTrusted = document.getElementById('manage-trusted');
const $trustedOverlay = document.getElementById('trusted-overlay');

export default class Host {
  constructor(peer) {
    this.peer = peer;
    // Every viewer that checks in gets an entry here so the host can broadcast
    // to several phones at once and show a live count. The QR stays available
    // the whole time (via "Add parent") so more phones can keep joining.
    this.connections = new Map();
    // Lightweight data channels used only to push the live viewer count to each
    // parent so they can see how many others are watching. Cosmetic, best-effort.
    this.dataConns = new Set();

    // Trusted-parent recognition. `promptedIds` dedupes prompts within this host
    // session (so reconnects/handoffs don't respam); `trustQueue` holds unknown
    // parents awaiting a heads-up prompt and `trustActive` is the one shown now,
    // so several unknown parents are surfaced one at a time (FIFO).
    this.promptedIds = new Set();
    this.trustQueue = [];
    this.trustActive = null;

    this.showQR(peer.id);
    this.initCryDetection();
    this.initBattery();
    this.initPresence();
    this.startCamera();
    this.bindControls();

    peer.on('call', (call) => {
      const meta = call.metadata || {};
      const answerWith = () =>
        activeVideoStream
          ? Promise.resolve(activeVideoStream)
          : navigator.mediaDevices.getUserMedia({ video: { frameRate: { ideal: 15, max: 20 } }, audio: { echoCancellation: true } });

      answerWith()
        .then((stream) => {
          call.answer(stream);
          // A viewer got through: re-arm peer-error tracking so a later
          // non-fatal blip is logged as a fresh event, not suppressed.
          notePeerRecovery();
          this.trackViewer(call, meta);
          this.considerTrust(meta.clientId, meta.name);
        })
        .catch((err) => console.error('[HOST] Failed to answer call', err));
    });

    // Parents open a data connection after connecting; use it to broadcast the
    // live viewer count so each parent can see how many others are watching.
    peer.on('connection', (conn) => {
      this.dataConns.add(conn);
      conn.on('open', () => {
        this.sendCount(conn);
        this.sendMelody(conn);
        this.sendBattery(conn);
        this.sendLive(conn);
      });
      // Parents can start/stop the lullaby remotely. The host owns the audio and
      // the authoritative "playing" state, so we just apply the request and
      // afterMelodyChange() broadcasts the result back to every parent. The
      // parent's own client already logged the analytics event, so we don't.
      conn.on('data', (msg) => {
        if (msg && msg.type === 'melody') this.setMelody(!!msg.playing);
      });
      conn.on('close', () => this.dataConns.delete(conn));
      conn.on('error', () => this.dataConns.delete(conn));
    });

    // A camera can stop on its own — the device sleeps, another app grabs it, or
    // the OS suspends the tab. When the page comes back to the foreground, make
    // sure the camera is still live and re-acquire it if not (see recoverCamera).
    // Also re-wire the cry detector: its shared AudioContext can slip to
    // 'suspended' while backgrounded, which would silently stop cry analysis.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.ensureCameraLive();
        if (this.cryDetector) this.cryDetector.resume();
      }
    });
  }

  bindControls() {
    $switchCamera.addEventListener('click', () => this.switchCamera());
    $copyLink.addEventListener('click', () => this.copyLink());
    $toggleVideo.addEventListener('click', () => this.setVideoDimmed(!this.videoDimmed));
    if ($videoOff) $videoOff.addEventListener('click', () => this.setVideoDimmed(false));
    if ($melodyToggle) $melodyToggle.addEventListener('click', () => this.toggleMelody());

    // "Add another parent" re-shows the QR card while live; tapping again hides.
    // Transition it so the QR card scales in/out over the live view rather than
    // popping, matching the morph used on the initial connect screen.
    $addParent.addEventListener('click', () => {
      // Decide the next state up front: the View Transitions update callback runs
      // asynchronously, so reading the toggle's result after it would be stale.
      // Flip the class and the button's pressed state together inside the
      // callback so the two can never desync.
      const showing = !document.body.classList.contains('show-qr');
      withViewTransition(() => {
        document.body.classList.toggle('show-qr', showing);
        setPressed($addParent, showing);
      });
    });

    if ($manageTrusted) $manageTrusted.addEventListener('click', () => this.openTrustedOverlay());
    if ($trustedOverlay) {
      $trustedOverlay.addEventListener('click', (e) => {
        if (e.target === $trustedOverlay || e.target.closest('.overlay-close')) {
          $trustedOverlay.hidden = true;
        }
        const removeBtn = e.target.closest('[data-remove-id]');
        if (removeBtn) this.removeTrusted(removeBtn.getAttribute('data-remove-id'));
      });
      // Persist a rename when the field loses focus or the value commits. Uses
      // 'change' (bubbles) so a single delegated listener covers every row.
      $trustedOverlay.addEventListener('change', (e) => {
        const input = e.target.closest('[data-rename-id]');
        if (input) this.renameTrusted(input.getAttribute('data-rename-id'), input.value);
      });
      // Enter commits (and blurs) the rename rather than doing nothing.
      $trustedOverlay.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target.closest('[data-rename-id]')) e.target.blur();
      });
    }
  }

  // ---- Trusted-parent recognition -----------------------------------------
  // Decide whether an incoming parent needs a heads-up. Trusted or
  // already-prompted parents (and metadata-less clients, which we can't save)
  // pass silently; a genuinely new parent is queued for a prompt.
  considerTrust(clientId, name) {
    if (!clientId) return; // metadata-less client: nothing to persist, stay silent
    if (isTrustedParent(clientId)) return;
    if (this.promptedIds.has(clientId)) return;
    if (this.trustQueue.some((q) => q.id === clientId)) return;
    this.promptedIds.add(clientId);
    this.trustQueue.push({ id: clientId, name });
    this.pumpTrustQueue();
  }

  // Show queued prompts one at a time so several new parents don't stack.
  pumpTrustQueue() {
    if (this.trustActive || !this.trustQueue.length) return;
    this.trustActive = this.trustQueue.shift();
    this.showTrustPrompt(this.trustActive.id, this.trustActive.name);
  }

  showTrustPrompt(clientId, name) {
    if (!$trustPrompt) return;
    const $text = $trustPrompt.querySelector('.trust-text');
    if ($text) {
      $text.textContent = name
        ? t('trust.newParentNamed', { name })
        : t('trust.newParent');
    }
    const $allow = $trustPrompt.querySelector('.trust-allow');
    const $ignore = $trustPrompt.querySelector('.trust-ignore');

    // Detach BOTH handlers on either choice, then fade out and (once hidden)
    // release the slot and reveal the next queued parent. Detaching both is
    // what keeps the unchosen button from firing for the next queued prompt.
    const onAllow = () => {
      addTrustedParent(clientId, name);
      track(EVENTS.TRUSTED_PARENT_ADDED, { total: getTrustedParents().length });
      if ($trustedOverlay && !$trustedOverlay.hidden) this.renderTrustedList();
      resolve();
    };
    const resolve = () => {
      clearTimeout(this.trustPromptTimer);
      if ($allow) $allow.removeEventListener('click', onAllow);
      if ($ignore) $ignore.removeEventListener('click', resolve);
      closeToast($trustPrompt, () => {
        this.trustActive = null;
        this.pumpTrustQueue();
      });
    };
    if ($allow) $allow.addEventListener('click', onAllow);
    if ($ignore) $ignore.addEventListener('click', resolve);
    // Falls through to the Ignore path so an unattended prompt never blocks the
    // queue or sticks on screen.
    this.trustPromptTimer = setTimeout(resolve, TRUST_PROMPT_MS);

    openToast($trustPrompt);
  }

  openTrustedOverlay() {
    if (!$trustedOverlay) return;
    this.renderTrustedList();
    $trustedOverlay.hidden = false;
  }

  // Render the saved trusted parents into the overlay, each with a Remove
  // button, or an empty-state line when none are saved yet.
  renderTrustedList() {
    if (!$trustedOverlay) return;
    const $list = $trustedOverlay.querySelector('.trusted-list');
    if (!$list) return;
    const parents = getTrustedParents();
    $list.replaceChildren();

    if (!parents.length) {
      const $empty = document.createElement('li');
      $empty.className = 'trusted-empty';
      $empty.textContent = t('trust.empty');
      $list.appendChild($empty);
      return;
    }

    parents.forEach((p) => {
      const $row = document.createElement('li');
      $row.className = 'trusted-row';

      // Editable label so the host can name each device something recognizable.
      const $name = document.createElement('input');
      $name.type = 'text';
      $name.className = 'trusted-name';
      $name.value = p.name || p.id;
      $name.setAttribute('data-rename-id', p.id);
      $name.setAttribute('aria-label', t('trust.nameAria'));
      $row.appendChild($name);

      const $remove = document.createElement('button');
      $remove.type = 'button';
      $remove.className = 'trusted-remove';
      $remove.setAttribute('data-remove-id', p.id);
      $remove.textContent = t('trust.remove');
      $row.appendChild($remove);

      $list.appendChild($row);
    });
  }

  // Forget a trusted parent. They can still connect (link possession is enough)
  // but will raise a fresh heads-up next time — so clear any session dedupe too.
  removeTrusted(id) {
    if (!id) return;
    removeTrustedParent(id);
    this.promptedIds.delete(id);
    track(EVENTS.TRUSTED_PARENT_REMOVED, { total: getTrustedParents().length });
    this.renderTrustedList();
  }

  // Save a host-chosen label. A blank value is ignored by the store; re-render
  // so the field snaps back to the persisted name rather than showing the blank.
  renameTrusted(id, name) {
    if (!id) return;
    renameTrustedParent(id, name);
    this.renderTrustedList();
  }

  // Register an answered viewer call: give it its own audio sink (so several
  // phones can be heard at once) and watch its connection so it's removed from
  // the count when it drops.
  //
  // Viewers are keyed by their peer id — which is STABLE across that parent's
  // network changes — rather than the per-call connectionId. When a parent's
  // phone hands off (e.g. Wi-Fi -> mobile) it tears down the old media call and
  // places a brand-new one with a fresh connectionId but the same peer id. Key
  // by peer id and adopt the replacement in place so the live count stays put
  // instead of briefly showing the same parent twice or flickering the number.
  trackViewer(call, meta = {}) {
    const id = call.peer;
    let entry = this.connections.get(id);

    if (entry) {
      // Keep the recognized identity fresh if the parent re-announced it.
      if (meta.clientId) { entry.clientId = meta.clientId; entry.name = meta.name || entry.name; }
      // Same parent reconnecting: swap in the new call, keep the existing audio
      // sink and the "active" flag so the count doesn't dip and recover.
      clearTimeout(entry.graceTimer);
      entry.graceTimer = null;
      const previous = entry.call;
      // Adopt the new call BEFORE closing the old one: MediaConnection.close()
      // emits 'close' synchronously, and dropViewer only bails when the closing
      // call is no longer the current one — so we must reassign first or we'd
      // evict the very viewer we're trying to keep.
      entry.call = call;
      if (previous && previous !== call) {
        try { previous.close(); } catch (e) { /* already closed */ }
      }
    } else {
      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      audioEl.setAttribute('playsinline', '');
      entry = { call, audioEl, active: false, graceTimer: null, iceRecovery: null, clientId: meta.clientId || null, name: meta.name || null };
      this.connections.set(id, entry);
    }

    this.bindCall(id, entry, call);
  }

  // Wire a (possibly replacement) call's media + connection-state events. Every
  // handler first checks it still owns the current call for this viewer, so a
  // late event from a superseded call can't disturb the live reconnection.
  bindCall(id, entry, call) {
    call.on('stream', (remoteStream) => {
      if (entry.call !== call) return;
      entry.audioEl.srcObject = remoteStream;
      if (!entry.audioEl.isConnected) document.body.appendChild(entry.audioEl);
      const wasActive = entry.active;
      entry.active = true;
      this.updateConnections();
      if (!wasActive) track(EVENTS.VIEWER_CONNECTED, { count: this.activeCount() });
    });

    const pc = call.peerConnection;
    if (pc) {
      // Answerer side of the ICE restart: the parent drives it, but we still need
      // this so PeerJS doesn't close our side on ICE failure, and so we answer the
      // parent's restart offer. onGiveUp is the normal viewer drop.
      if (entry.iceRecovery) entry.iceRecovery.destroy();
      entry.iceRecovery = attachIceRecovery(pc, {
        peer: this.peer,
        remoteId: call.peer,
        connectionId: call.connectionId,
        role: 'answerer',
        onGiveUp: () => this.dropViewer(id, call),
      });
      pc.addEventListener('connectionstatechange', () => {
        if (entry.call !== call) return;
        const state = pc.connectionState;
        const recover = () => (entry.iceRecovery ? entry.iceRecovery.recover('conn-' + state) : this.dropViewer(id, call));
        if (state === 'connected') {
          clearTimeout(entry.graceTimer);
          // Cap this viewer's encoder now that negotiation is done (encodings are
          // only populated post-negotiation on some browsers).
          applyVideoSendCaps(pc);
        } else if (state === 'closed') {
          // A real teardown (tab closed / hung up): drop immediately so the count
          // doesn't sit inflated for the grace window on every normal exit.
          this.dropViewer(id, call);
        } else if (state === 'failed') {
          recover();
        } else if (state === 'disconnected') {
          clearTimeout(entry.graceTimer);
          entry.graceTimer = setTimeout(() => {
            if (entry.call === call && ['disconnected', 'failed'].includes(pc.connectionState)) {
              recover();
            }
          }, 4000);
        }
      });
    }
    call.on('close', () => {
      if (entry.iceRecovery && entry.iceRecovery.isRecovering()) return;
      this.dropViewer(id, call);
    });
  }

  dropViewer(id, call) {
    const entry = this.connections.get(id);
    if (!entry) return;
    // Ignore a late close/failed from a call the parent has already replaced —
    // dropping here would wrongly evict the viewer that's now live again.
    if (call && entry.call !== call) return;
    const wasActive = entry.active;
    clearTimeout(entry.graceTimer);
    if (entry.iceRecovery) { entry.iceRecovery.destroy(); entry.iceRecovery = null; }
    try { entry.audioEl.srcObject = null; entry.audioEl.remove(); } catch (e) { /* already gone */ }
    try { entry.call.close(); } catch (e) { /* already closed */ }
    this.connections.delete(id);
    this.updateConnections();
    if (wasActive) track(EVENTS.VIEWER_DISCONNECTED, { count: this.activeCount() });
  }

  activeCount() {
    return [...this.connections.values()].filter((e) => e.active).length;
  }

  sendCount(conn) {
    try {
      if (conn && conn.open) conn.send({ type: 'count', count: this.activeCount() });
    } catch (e) {
      /* best-effort */
    }
  }

  // Reflect the number of live viewers: hide the QR card once someone is
  // watching (re-openable via "Add parent"), show the status pill, and push the
  // count to every connected parent.
  updateConnections() {
    const count = this.activeCount();
    // Only animate when the live view itself appears/disappears (the 0<->1
    // boundary); a parent merely joining/leaving while others watch changes no
    // screen, so it must not trigger a needless cross-fade flash.
    const wasLive = document.body.classList.contains('has-stream');
    const nowLive = count > 0;
    const swap = () => {
      document.body.classList.toggle('has-stream', nowLive);
      if (count === 0) document.body.classList.remove('show-qr');
    };
    if (wasLive !== nowLive) withViewTransition(swap);
    else swap();

    if ($hostStatus) {
      $hostStatus.classList.toggle('live', count > 0);
      const $text = $hostStatus.querySelector('.status-text');
      if ($text) {
        $text.textContent = count === 0
          ? t('hostStatus.waiting')
          : tCount('hostStatus.count', count);
      }
    }

    this.dataConns.forEach((conn) => this.sendCount(conn));
  }

  async startCamera() {
    try {
      // Prefer the stream primed during the role tap (see primeCamera); only
      // request fresh if there wasn't one (e.g. an unexpected entry path).
      const primed = takePrimedCamera();
      const stream = primed
        ? await primed
        : await navigator.mediaDevices.getUserMedia({
            video: { frameRate: { ideal: 15, max: 20 } },
            audio: { echoCancellation: true },
          });
      // A prior error screen (denied/busy camera) gets restored to the normal
      // QR card here, so a successful retry doesn't leave the error stuck up.
      this.cameraError = false;
      clearConnectError();
      this.useStream(stream);
      this.createConstraints();
      track(EVENTS.CAMERA_STARTED);
    } catch (err) {
      console.error('[HOST] Could not access camera/microphone', err);
      track(EVENTS.CAMERA_ERROR, { name: err && err.name });
      this.cameraError = true;
      this.armCameraRefocusRetry(err);
      showConnectError(t(cameraErrorKey(err)), {
        retryLabel: t('common.retry'),
        onRetry: () => this.startCamera(),
        backLabel: t('common.back'),
      });
    }
  }

  // A "camera busy" failure (another app holds it, or the OS suspended it) is
  // usually transient: the user closes the other app and comes back. Retry
  // automatically the moment the monitor tab is foregrounded again, so they
  // don't have to notice and tap Retry. Bound once; guarded by cameraError so
  // it only fires while an error is actually showing.
  armCameraRefocusRetry(err) {
    const name = err && err.name;
    const busy = name === 'NotReadableError' || name === 'AbortError' || name === 'TrackStartError';
    if (!busy || this._cameraRetryBound) return;
    this._cameraRetryBound = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.cameraError) this.startCamera();
    });
  }

  useStream(stream) {
    showAudioLevels(stream);
    $video.muted = true;
    $video.srcObject = stream;
    activeVideoStream = stream;
    playVideo($video);
    this.watchStreamHealth(stream);
    this.startLiveHeartbeat();
    if (this.cryDetector) this.cryDetector.attach(stream);
  }

  // Broadcast a "live video" heartbeat carrying a counter of genuine camera
  // frames, so parents can verify the source is producing new pictures rather
  // than a frozen frame being re-encoded (which would still advance byte and
  // decoded-frame counters on their side).
  //
  // The counter comes from requestVideoFrameCallback on the local preview, which
  // ticks once per frame the capture pipeline actually delivers: a still, quiet
  // nursery still counts (frames keep arriving), but a stalled/frozen camera does
  // not. Where rVFC isn't available we fall back to the encoder's framesEncoded
  // from getStats, which stops climbing the same way when the source freezes.
  // rVFC is bound to the <video> element, so it keeps counting across camera
  // switches/recovery without re-arming; the guard makes repeat calls a no-op.
  startLiveHeartbeat() {
    if (this._liveHeartbeatStarted) return;
    this._liveHeartbeatStarted = true;
    this.frameSeq = 0;

    const v = $video;
    if (v && typeof v.requestVideoFrameCallback === 'function') {
      this._frameSource = 'rvfc';
      const onFrame = () => {
        this.frameSeq += 1;
        try { v.requestVideoFrameCallback(onFrame); } catch (e) { /* element gone */ }
      };
      try { v.requestVideoFrameCallback(onFrame); } catch (e) { /* ignore */ }
    } else {
      this._frameSource = 'stats';
    }

    this.liveHeartbeatTimer = setInterval(() => this.broadcastLive(), LIVE_HEARTBEAT_MS);
  }

  async broadcastLive() {
    // Only vouch for a live source while the camera track is actually live and
    // the preview is playing. Otherwise (paused, or a track ended and being
    // recovered) stay silent: a heartbeat whose counter isn't advancing would
    // wrongly read as a frozen camera on the parent, so we'd rather send nothing
    // and let the parent fall back to its own inbound-frame check.
    const vtrack = activeVideoStream
      && activeVideoStream.getVideoTracks
      && activeVideoStream.getVideoTracks()[0];
    const sourceUp = vtrack && vtrack.readyState === 'live' && $video && !$video.paused;
    if (!sourceUp) return;

    // rVFC only ticks while the page is actually presenting frames, so it stalls
    // when the monitor runs with its screen off / backgrounded — the *normal* way
    // a baby unit is left — even though the camera keeps streaming real frames to
    // parents. Trusting the frozen rVFC counter then raises a false "camera
    // frozen" over a visibly moving picture. Whenever rVFC can't be trusted (no
    // rVFC support, or the page is hidden) sample the encoder's framesEncoded
    // instead: it keeps climbing as long as frames are genuinely going out, and
    // still flattens on a real source freeze.
    if (this._frameSource === 'stats' || document.hidden) {
      const encoded = await this.readEncodedFrames();
      if (encoded != null) this.frameSeq = encoded;
    }
    this.dataConns.forEach((conn) => this.sendLive(conn));
  }

  // Highest framesEncoded across any active outbound video sender. Any live
  // parent connection encodes the same camera, so the first usable number wins.
  async readEncodedFrames() {
    const byPeer = (this.peer && this.peer.connections) || {};
    for (const conns of Object.values(byPeer)) {
      for (const conn of (conns || [])) {
        const pc = conn && conn.peerConnection;
        if (!pc || typeof pc.getStats !== 'function') continue;
        try {
          const stats = await pc.getStats();
          let frames = null;
          stats.forEach((r) => {
            if (r.type === 'outbound-rtp'
              && (r.kind || r.mediaType) === 'video'
              && typeof r.framesEncoded === 'number') {
              frames = Math.max(frames || 0, r.framesEncoded);
            }
          });
          if (frames != null) return frames;
        } catch (e) { /* try the next connection */ }
      }
    }
    return null;
  }

  // Best-effort, mirrors sendCount: tell one parent the current camera frame
  // counter so it can confirm the source is genuinely live.
  sendLive(conn) {
    try {
      if (conn && conn.open) conn.send({ type: 'live', seq: this.frameSeq });
    } catch (e) {
      /* best-effort */
    }
  }

  // Battery saver: cover the local preview with a black overlay so the phone's
  // own screen stops rendering the picture (helps a lot on OLED). The camera
  // track keeps running and streaming to parents — mobile browsers pause
  // getUserMedia if the screen truly sleeps, so we dim rather than sleep, and
  // the wake lock stays on.
  setVideoDimmed(on) {
    this.videoDimmed = on;
    document.body.classList.toggle('video-dim', on);
    if ($videoOff) $videoOff.hidden = !on;
    setPressed($toggleVideo, on);
    if (on) track(EVENTS.VIDEO_DIMMED);
  }

  async copyLink() {
    const label = $copyLink.querySelector('.copy-label');
    if (await copyWithFeedback(hostLink(this.peer.id), label)) track(EVENTS.LINK_COPIED);
  }

  // Play a soothing lullaby out of the baby monitor's own speaker so it reaches
  // the baby in the room. It loops until stopped. Either the baby monitor or any
  // connected parent can start/stop it: the host owns the audio and the playing
  // state and broadcasts every change to all parents so their controls and the
  // subtle "playing" indicator stay in sync (see afterMelodyChange).
  ensureMelody() {
    if (this.melody) return;
    // Reuse the singleton primed at role-pick time (see primeMelody). That
    // earlier gesture is what lets a parent's remote command start playback;
    // without it, only a local tap on the monitor could.
    this.melody = getMelodyAudio();
    // Reflect any state change (including the OS pausing us) everywhere.
    this.melody.addEventListener('play', () => this.afterMelodyChange(true));
    this.melody.addEventListener('pause', () => this.afterMelodyChange(false));
  }

  melodyPlaying() {
    return !!(this.melody && !this.melody.paused);
  }

  // The baby monitor's own button: flip to the opposite of the current state.
  toggleMelody() {
    const next = !this.melodyPlaying();
    track(EVENTS.MELODY_TOGGLED, { playing: next, source: 'host' });
    this.setMelody(next);
  }

  setMelody(on) {
    this.ensureMelody();
    if (on) {
      // Priming (see primeMelody) leaves the element muted-then-restored; make
      // sure real playback is audible regardless of any in-flight priming state.
      this.melody.muted = false;
      // A parent's remote request isn't a local gesture; if the host browser
      // ever blocks playback, fall back to "stopped" so no control lies. Success
      // is handled by the 'play' listener, so we only correct on failure here.
      this.melody.play().catch((err) => {
        console.warn('[HOST] Melody playback failed', err && err.name);
        this.afterMelodyChange(false);
      });
    } else {
      // Success flows through the 'pause' listener; nothing else to do.
      this.melody.pause();
    }
  }

  // Single funnel for every state change (button, remote command, the auto-stop
  // timer, or the OS pausing us), so it runs exactly once per change.
  afterMelodyChange(playing) {
    this.reflectMelody(playing);
    this.broadcastMelody(playing);
    // Mute cry detection while the lullaby plays: the melody bleeds into the
    // host mic and would otherwise be flagged as sustained crying, pushing false
    // alerts to parents. Resume detection the moment it stops.
    this.setCryDetection(!playing);
    this.armMelodyAutoStop(playing);
  }

  // Since cry alerts are paused while the lullaby plays, don't let it run
  // forever — stop it after MELODY_MAX_MS so detection always comes back.
  armMelodyAutoStop(playing) {
    clearTimeout(this.melodyTimer);
    this.melodyTimer = playing
      ? setTimeout(() => this.setMelody(false), MELODY_MAX_MS)
      : null;
  }

  setCryDetection(on) {
    if (!this.cryDetector) return;
    if (on) this.cryDetector.start();
    else this.cryDetector.stop();
  }

  reflectMelody(playing) {
    setPressed($melodyToggle, playing);
    // Drives the shared "lullaby playing / cry alerts paused" indicator, shown
    // on the baby monitor too (not just parents).
    document.body.classList.toggle('melody-playing', playing);
  }

  broadcastMelody(playing) {
    this.dataConns.forEach((conn) => this.sendMelody(conn, playing));
  }

  // Best-effort, mirrors sendCount: tell one parent whether the lullaby is on.
  sendMelody(conn, playing) {
    const state = playing != null ? playing : this.melodyPlaying();
    try {
      if (conn && conn.open) conn.send({ type: 'melody', playing: state });
    } catch (e) {
      /* best-effort */
    }
  }

  watchStreamHealth(stream) {
    stream.getTracks().forEach((track) => {
      track.addEventListener('ended', () => this.recoverCamera(), { once: true });
    });
    // iOS interrupts the capture session — the system Low Battery alert (at 20%
    // and 10%), an incoming call, Control Centre — by MUTING the video track
    // rather than ending it, then UNMUTES it once the interruption clears. The
    // 'ended' handler above never fires for this, so without handling mute the
    // preview and every viewer sit frozen until someone physically wakes the
    // baby monitor (exactly the battery-alert problem). Resume automatically the
    // instant iOS lets go, and if it stays muted, force a re-acquire.
    const video = stream.getVideoTracks()[0];
    if (video) {
      video.addEventListener('mute', () => this.onCameraInterrupted());
      video.addEventListener('unmute', () => this.onCameraResumed());
    }
  }

  // Capture interruption began. Give iOS a brief grace to clear it on its own
  // (the battery alert auto-dismisses after a few seconds), then, if the track is
  // still muted, force recovery — retrying on a short cadence because
  // getUserMedia keeps rejecting while the device is held by the interruption.
  onCameraInterrupted() {
    track(EVENTS.CAMERA_INTERRUPTED);
    clearTimeout(this._camInterruptTimer);
    this._camInterruptRetries = 0;
    this._camInterruptTimer = setTimeout(() => this.pollCameraRecovery(), 1800);
  }

  async pollCameraRecovery() {
    const vtrack = activeVideoStream && activeVideoStream.getVideoTracks()[0];
    // Resumed on its own (the unmute handler ran) — nothing to force.
    if (!vtrack || (!vtrack.muted && vtrack.readyState === 'live')) return;
    // Don't hammer getUserMedia while backgrounded: it can't succeed there and
    // the visibility/unmute handlers already cover coming back. Keep the retry
    // loop alive so we act as soon as we're foreground again.
    if (document.visibilityState !== 'visible') {
      if (this._camInterruptRetries < 15) {
        this._camInterruptTimer = setTimeout(() => this.pollCameraRecovery(), 2000);
      }
      return;
    }
    this._camInterruptRetries += 1;
    // getUserMedia rejects with NotReadableError while the device is still held;
    // recoverCamera swallows that, so we just re-check and retry.
    await this.recoverCamera();
    const still = activeVideoStream && activeVideoStream.getVideoTracks()[0];
    const recovered = still && !still.muted && still.readyState === 'live';
    if (!recovered && this._camInterruptRetries < 15) {
      this._camInterruptTimer = setTimeout(() => this.pollCameraRecovery(), 2000);
    }
  }

  // Interruption cleared and iOS handed the original track back. It usually
  // leaves the <video> paused, so just make sure the preview is playing again;
  // the same track keeps flowing to every viewer, so nothing needs re-negotiating.
  onCameraResumed() {
    clearTimeout(this._camInterruptTimer);
    this._camInterruptRetries = 0;
    playVideo($video);
  }

  async recoverCamera() {
    if (this.recovering) return;
    this.recovering = true;
    const previous = activeVideoStream;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { frameRate: { ideal: 15, max: 20 } },
        audio: { echoCancellation: true },
      });
      this.replaceOutgoingTracks(stream);
      this.useStream(stream);
      if (previous && previous !== stream) {
        previous.getTracks().forEach((t) => t.stop());
      }
      track(EVENTS.CAMERA_RECOVERED);
    } catch (err) {
      console.warn('[HOST] Camera recovery failed, will retry on next focus:', err && err.name);
    } finally {
      this.recovering = false;
    }
  }

  replaceOutgoingTracks(stream) {
    const connectionsByPeer = (this.peer && this.peer.connections) || {};
    Object.values(connectionsByPeer).forEach((connections) => {
      (connections || []).forEach((conn) => {
        const pc = conn && conn.peerConnection;
        if (!pc || !pc.getSenders) return;
        pc.getSenders().forEach((sender) => {
          if (!sender.track) return;
          const replacement = stream.getTracks().find((t) => t.kind === sender.track.kind);
          if (replacement) sender.replaceTrack(replacement).catch(() => {});
        });
        // replaceTrack resets sender parameters, so re-assert the caps on the new
        // track (camera switch / recovery both route through here).
        applyVideoSendCaps(pc);
      });
    });
  }

  async switchCamera() {
    if (this.recovering) return;
    const next = nextCameraConstraints();
    if (!next) return;
    this.recovering = true;
    const previous = activeVideoStream;
    // Android (e.g. Pixel 5) refuses to open the second camera while the first
    // is still live, so release the current video device before requesting the
    // next one.
    if (previous) previous.getVideoTracks().forEach((t) => t.stop());
    let switched = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia(next);
      // We now hold a live camera. Mark the switch done before adopting it so an
      // (unlikely) throw in the swap/adopt path can't drop us into the recovery
      // fallback below, which would acquire a second stream and leak this one.
      switched = true;
      this.replaceOutgoingTracks(stream);
      this.useStream(stream);
      if (previous && previous !== stream) {
        previous.getTracks().forEach((t) => t.stop());
      }
      track(EVENTS.CAMERA_SWITCHED);
    } catch (err) {
      console.error('[HOST] Camera switch failed', err);
      track(EVENTS.CAMERA_ERROR, { name: err && err.name });
    } finally {
      this.recovering = false;
    }
    // We already stopped the old camera, so a failed switch would otherwise
    // leave the preview and every viewer black until the next focus event.
    // Re-acquire an available camera right away instead of waiting. (The guard
    // was cleared above, so recoverCamera can run.)
    if (!switched) await this.recoverCamera();
  }

  ensureCameraLive() {
    // While a camera error is showing, startCamera()'s own refocus retry owns
    // recovery (and restores the error card on success) — don't also fire
    // recoverCamera here, or we'd request the camera twice on the same focus.
    if (this.cameraError) return;
    const track = activeVideoStream && activeVideoStream.getVideoTracks()[0];
    if (!track || track.readyState === 'ended') this.recoverCamera();
  }

  // Analyse the host's own microphone and ask the Worker to push a cry alert to
  // every subscribed viewer, even when no viewer is connected — the Worker
  // simply has no subscriptions to notify — so background alerts don't depend
  // on the app being open anywhere. The detector calibrates itself against the
  // room's ambient level, so there's nothing to tune here (viewer sensitivity no
  // longer exists) and background reliability is unaffected by the UI.
  initCryDetection() {
    this.cryDetector = createCryDetector({
      onCry: () => notifyCry(this.peer.id),
    });
    this.cryDetector.start();
  }

  // ---- Battery warning -----------------------------------------------------
  // The Battery Status API is Android/Chrome-only (iOS/Safari removed it), so on
  // an iOS monitor getBattery is absent and no warning is ever emitted — which
  // is fine: the feature targets Android monitors. We only push state to parents
  // over the data channel; the monitor itself stays silent (no local UI/sound).
  initBattery() {
    if (!navigator.getBattery) return;
    navigator.getBattery().then((b) => {
      this.battery = b;
      // Seed the "was it low?" edge tracker, then broadcast only on transitions
      // so a healthy battery ticking down 1% at a time doesn't spam the channel.
      this.batteryWasLow = this.batteryLow();
      ['levelchange', 'chargingchange'].forEach((e) =>
        b.addEventListener(e, () => this.onBatteryChange())
      );
      this.onBatteryChange();
    }).catch(() => { /* getBattery can reject; just skip the feature */ });
  }

  batteryLow() {
    return !!(this.battery && !this.battery.charging && this.battery.level < BATTERY_LOW);
  }

  batteryState() {
    return {
      type: 'battery',
      low: this.batteryLow(),
      level: this.battery ? Math.round(this.battery.level * 100) : null,
      charging: !!(this.battery && this.battery.charging),
    };
  }

  // Keep the data channel quiet at healthy levels. levelchange fires every 1%,
  // so we only broadcast when it matters: while low (to keep the parent's
  // percentage fresh) and on the single false transition that clears the pill.
  // A steady, healthy battery ticking down sends nothing.
  onBatteryChange() {
    const low = this.batteryLow();
    const flipped = low !== this.batteryWasLow;
    if (flipped && low) track(EVENTS.BATTERY_LOW, { level: Math.round(this.battery.level * 100) });
    this.batteryWasLow = low;
    if (low || flipped) this.broadcastBattery();
  }

  broadcastBattery() {
    this.dataConns.forEach((conn) => this.sendBattery(conn));
  }

  // ---- Presence ------------------------------------------------------------
  // Presence is inferred server-side from the live signaling socket (the
  // PeerSignal DO reports the monitor as present while it holds that socket), so
  // there's no periodic heartbeat to send. We only announce a *clean* exit as the
  // page unloads, so a deliberate close isn't mistaken for an outage. pagehide is
  // the most reliable "page going away" signal on mobile (fires for unload and
  // bfcache); a crash / power loss / dead battery fires nothing, so the socket
  // simply drops and the scheduled sweep surfaces it as "monitor offline".
  initPresence() {
    window.addEventListener('pagehide', () => sendGoodbye(this.peer.id));
  }

  // Best-effort, mirrors sendMelody/sendCount. A parent that connects before
  // getBattery() resolves gets low:false/level:null here; onBatteryChange()
  // corrects it once the promise settles.
  sendBattery(conn) {
    try {
      if (conn && conn.open) conn.send(this.batteryState());
    } catch (e) {
      /* best-effort */
    }
  }

  showQR(connectId) {
    renderQR($connect.querySelector('.qr'), hostLink(connectId));
  }

  createConstraints() {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      let cameras = devices.filter((device) => device.kind == 'videoinput');
      constraints = setConstraints(cameras);
      if (cameras.length > 1) document.body.classList.add('multi-camera');
    }).catch(function (err) {
      console.log(err.name + ': ' + err.message);
    });
  }
}
